import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';

import AddIcon from '@mui/icons-material/Add';

import Dialog from '@shared/molecules/Dialog';
import Button from '@shared/atoms/Button';
import SearchField from '@shared/atoms/SearchField';
import Snackbar from '@shared/atoms/Snackbar';
import T from '@shared/atoms/Typography';

import theme from '@shared/themes/default';

import { useAppGlobals } from '../../contexts/AppContext';

import ExpertsList from './components/ExpertsList';
import ExpertDrawer from './components/ExpertDrawer';
import { createExpert, deleteExpert, updateExpert } from './graphql/mutations';
import { listExperts, listSocialMediaTypes } from './graphql/queries';
import { CORE_EXPERT_FIELDS } from './graphql/fragments';

const OuterWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.m1};
  max-width: 650px;
`;

const HeaderWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  column-gap: ${theme.spacing.s};
`;

const ExpertsView = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedExpert, setSelectedExpert] = useState();
  const [toDeleteExpert, setToDeleteExpert] = useState();
  const [deleteExpertDialog, setDeleteExpertDialog] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [displayError, setDisplayError] = useState('');
  const [detailedErrors, setDetailedErrors] = useState();

  const { locale, useGraphQLMutation, useNewGraphQLQuery } = useAppGlobals();
  const { t } = useTranslation();

  const [searchParams] = useSearchParams();
  const deepLinkedExpertId = searchParams.get('expert');
  const hasDeepLinkedRef = useRef(false);

  const expertsLimit = 100;

  const handleCloseDrawer = () => {
    setSelectedExpert();
  };

  const {
    data: expertsData,
    loading: expertsLoading,
    fetchMore,
  } = useNewGraphQLQuery(listExperts, {
    variables: {
      pagination: { limit: expertsLimit, offset: 0 },
    },
  });
  const listSocialMediaTypesResponse = useNewGraphQLQuery(listSocialMediaTypes);

  const [createExpertMutation, createExpertMutationState] = useGraphQLMutation(
    createExpert,
    {
      update(cache, { data: { createExpert } }) {
        cache.modify({
          fields: {
            listExperts(existingExperts = []) {
              const newExpertRef = cache.writeFragment({
                data: createExpert,
                fragment: CORE_EXPERT_FIELDS,
              });
              return [...existingExperts, newExpertRef];
            },
          },
        });
      },
      onCompleted() {
        handleCloseDrawer();
      },
      onError({ message }) {
        if (message) {
          setDisplayError(t('backoffice.experts.errorSaving'));
          const detailedError = [
            {
              title: t('backoffice.experts.errorSaving'),
              description: message,
            },
          ];
          setDetailedErrors(detailedError);
        }
      },
    }
  );

  const [updateExpertMutation, updateExpertMutationState] = useGraphQLMutation(
    updateExpert,
    {
      onCompleted() {
        handleCloseDrawer();
      },
      onError({ message }) {
        if (message) {
          setDisplayError(t('backoffice.experts.errorSaving'));
          const detailedError = [
            {
              title: t('backoffice.experts.errorSaving'),
              description: message,
            },
          ];
          setDetailedErrors(detailedError);
        }
      },
    }
  );

  const [deleteExpertMutation, deleteExpertMutationState] = useGraphQLMutation(
    deleteExpert,
    {
      update(cache, { data: { deleteExpert } }) {
        cache.modify({
          fields: {
            listExperts(existingExperts = [], { readField } = {}) {
              return existingExperts.filter(
                (expertRef) => deleteExpert.id !== readField('id', expertRef)
              );
            },
          },
        });
      },
      onError() {
        setDisplayError(t('backoffice.experts.errorDeleting'));
      },
    }
  );

  const handleCreateExpert = () => {
    setSelectedExpert({});
  };

  const handleDeleteExpertDialog = (id) => {
    setToDeleteExpert(
      expertsData.listExperts.find((expert) => expert.id === id)
    );
    setDeleteExpertDialog(true);
  };

  const handleDeleteExpert = () => {
    deleteExpertMutation({ variables: { id: toDeleteExpert.id } });
    setDeleteExpertDialog(false);
  };

  useEffect(() => {
    setDrawerOpen(!!selectedExpert);
  }, [selectedExpert]);

  useEffect(() => {
    if (hasDeepLinkedRef.current || !deepLinkedExpertId || expertsLoading) {
      return;
    }
    const match = expertsData?.listExperts?.find(
      (expert) => String(expert.id) === deepLinkedExpertId
    );
    if (match) {
      hasDeepLinkedRef.current = true;
      setSelectedExpert(match);
    }
  }, [deepLinkedExpertId, expertsLoading, expertsData]);

  useEffect(() => {
    if (
      !expertsLoading &&
      expertsData.listExperts.length > 0 &&
      expertsData.listExperts.length % expertsLimit === 0
    ) {
      fetchMore({
        variables: {
          pagination: {
            limit: expertsLimit,
            offset: expertsData.listExperts.length,
          },
        },
      });
    }
  }, [expertsLoading, expertsData?.listExperts?.length]);

  const isLocked =
    expertsLoading ||
    listSocialMediaTypesResponse.loading ||
    deleteExpertMutationState.loading;

  const filteredExperts = (
    !searchValue
      ? [...(expertsData?.listExperts || [])]
      : (expertsData?.listExperts || []).filter(
          (expert) =>
            expert.firstName
              .toLowerCase()
              .includes(searchValue.toLowerCase()) ||
            expert.lastName.toLowerCase().includes(searchValue.toLowerCase())
        )
  ).sort((a, b) => a.lastName.localeCompare(b.lastName, locale));

  const sortedSocialMediaTypes = [
    ...(listSocialMediaTypesResponse.data?.listSocialMediaTypes || []),
  ].sort((a, b) => a.name.localeCompare(b.name, locale));

  return (
    <OuterWrapper>
      <Snackbar
        type="error"
        message={displayError}
        open={!!displayError}
        onClose={() => setDisplayError('')}
        detailedErrors={detailedErrors}
      />
      {deleteExpertDialog && (
        <Dialog
          actions={[
            {
              ariaLabel: t('generic.cancel'),
              handler: () => {
                setDeleteExpertDialog(false);
              },
              label: t('generic.cancel'),
              type: 'secondaryOutline',
            },
            {
              ariaLabel: t('backoffice.experts.delete'),
              handler: handleDeleteExpert,
              label: t('backoffice.experts.delete'),
            },
          ]}
          ariaLabel={t('backoffice.experts.deleteTitle')}
          onClose={() => setDeleteExpertDialog(false)}
          open={deleteExpertDialog}
          sx={{ '.MuiDialogContent-root': { overflowY: 'unset' } }}
          title={t('backoffice.experts.deleteTitle')}
        >
          <T
            color={theme.palette.mainDark}
            fontWeight="semibold"
            sx={{ textAlign: 'center' }}
            variant="subtitle"
          >
            {t('backoffice.experts.deleteText', {
              name: `${toDeleteExpert.firstName} ${toDeleteExpert.lastName}`,
            })}
          </T>
        </Dialog>
      )}
      <HeaderWrapper>
        <SearchField
          ariaLabel={t('backoffice.experts.search')}
          disabled={isLocked}
          loading={isLocked}
          onChange={({ value }) => setSearchValue(value)}
          onClear={() => setSearchValue('')}
          placeholder={t('backoffice.experts.search')}
          theme="dark"
          type="primary"
          value={searchValue}
        />
        <Button
          ariaLabel={t('backoffice.experts.add')}
          iconRight={AddIcon}
          onClick={handleCreateExpert}
          sx={{ flex: '0 0 auto' }}
        >
          {t('backoffice.experts.add')}
        </Button>
      </HeaderWrapper>
      <ExpertsList
        experts={filteredExperts}
        filter={searchValue}
        handleDeleteExperts={handleDeleteExpertDialog}
        isLocked={isLocked}
        loading={expertsLoading}
        selectedExpert={selectedExpert}
        setSelectedExpert={setSelectedExpert}
      />
      <ExpertDrawer
        expert={selectedExpert}
        handleClose={handleCloseDrawer}
        handleCreate={createExpertMutation}
        handleCreateEnded={
          createExpertMutationState.called && !createExpertMutationState.loading
        }
        handleUpdate={updateExpertMutation}
        handleUpdateEnded={
          updateExpertMutationState.called && !updateExpertMutationState.loading
        }
        open={drawerOpen}
        showError={!!displayError}
        socialMediaTypes={sortedSocialMediaTypes}
      />
    </OuterWrapper>
  );
};

export default ExpertsView;
